import { api, customerReqConfig } from "../../src/include/api";
import { ERROR, SUCCESS } from "../../src/include/constant";

// Load Cart Data and Map
export const loadCartData = async (
  customerId,
  setCartData,
  setAddedToCartMap,
) => {
  try {
    const res = await api.get(
      `/cart/?customerId=${customerId}`,
      customerReqConfig(),
    );
    const cart = res.data.data || [];
    console.log("cart", cart);
    setCartData(cart);
    const addedMap = {};
    const cartItems = Array.isArray(cart[0]?.cartItems)
      ? cart[0].cartItems
      : [];
    cartItems.forEach((item) => {
      addedMap[item.productId] = true;
    });
    setAddedToCartMap(addedMap);
  } catch {
    setCartData([]);
    setAddedToCartMap({});
  }
};

export const loadWishlist = async (
  customerId,
  setWishlistProductIds,
  setTotalWishlistItems,
  setFetchLoading,
) => {
  try {
    const res = await api.get(
      `/wishlist/?customerId=${customerId}`,
      customerReqConfig(),
    );
    const ids = res.data.data.products?.map((p) => String(p.id)) || [];
    setWishlistProductIds(ids);
    setTotalWishlistItems(ids.length);
  } catch {
    setWishlistProductIds([]);
  } finally {
    setFetchLoading(false);
  }
};

export const addToCartHandler = async ({
  product,
  customer,
  setCartData: optionalSetCartData,
  setSavedForLaterData,
  setAddedToCartMap,
  fetchCartCount,
  toast,
  navigate,
  setLoading,
  savedForLaterData,
}) => {
  if (!customer || !customer.id) {
    navigate("/login");
    return;
  }
  const customerId = customer.id;
  const productId = product.productId || product.id;

  setLoading(product.id || productId);
  const isB2C = !customer || customer.customerType === "individual";

  try {
    // Fetch current cart
    let currentCart = null;
    try {
      const res = await api.get(
        `/cart/?customerId=${customerId}`,
        customerReqConfig(),
      );
      const carts = Array.isArray(res.data.data) ? res.data.data : [];
      currentCart = carts.length > 0 ? carts[0] : null;
    } catch (err) {
      console.error("Failed to fetch cart:", err);
    }

    // Check duplicate
    const selectedOption = product.selectedOption;
    let variantId = selectedOption?.value?.id || null;

    if (
      currentCart?.cartItems?.some((item) => {
        const sameProduct = String(item.productId) === String(productId);
        const sameVariant = variantId
          ? String(item.variantId) === String(variantId)
          : true;
        return sameProduct && sameVariant;
      })
    ) {
      toast({ type: "info", message: "Already in cart" });
      setLoading(null);
      return;
    }

    // Remove from Saved For Later if exists
    let latestSaved = savedForLaterData?.products || [];
    if (!latestSaved.length) {
      try {
        const res = await api.get(
          `/save_for_later/?customerId=${customerId}`,
          customerReqConfig(),
        );
        latestSaved = res?.data?.data?.products || [];
      } catch (err) {
        console.warn("Failed to load saved for later", err);
      }
    }
    if (latestSaved.some((p) => String(p.id) === String(productId))) {
      await api.delete(
        `/save_for_later/?customerId=${customerId}&productId=${productId}`,
        customerReqConfig(),
      );
      setSavedForLaterData?.((prev) => ({
        ...prev,
        products:
          prev.products?.filter((p) => String(p.id) !== String(productId)) ||
          [],
      }));
    }

    // ✅=== PRICING LOGIC START ===✅
    let price = 0; // Will be Retail/MRP or Selling Price based on logic
    let discountMode = null;
    let discountValue = 0; // RAW value (not calculated)
    let deliveryCharge = parseFloat(product.deliveryCharge) || 0;
    let itemName = product.name;
    let qty = 1;
    let variantName = null;

    // --- LOGIC 2: qtyAndPrice (Bulk Pricing) ---
    if (product.qtyAndPrice && product.qtyAndPrice.length > 0) {
      let selectedQtyEntry;

      // ✅ Check if user selected a specific option from modal
      if (selectedOption?.type === "qtyAndPrice" && selectedOption?.value) {
        selectedQtyEntry = selectedOption.value;
      } else {
        // Fallback: Find minimum qty (for direct add without modal)
        selectedQtyEntry = product.qtyAndPrice.reduce(
          (minEntry, currentEntry) => {
            return currentEntry.minQty < minEntry.minQty
              ? currentEntry
              : minEntry;
          },
          product.qtyAndPrice[0],
        );
      }

      if (selectedQtyEntry) {
        // ✅ Price is ALREADY selling price - NO discount calculation
        price = isB2C
          ? parseFloat(selectedQtyEntry.setB2cPrice) || 0
          : parseFloat(selectedQtyEntry.setB2bPrice) || 0;
        discountMode = null; // No additional discount
        discountValue = 0;
        qty = selectedQtyEntry.minQty || 1; // ✅ Use selected minQty (100, not 50)
        variantName = `${selectedQtyEntry.minQty}-${selectedQtyEntry.maxQty} ${selectedQtyEntry.basedOn}`;
        variantId = selectedQtyEntry.id; // ✅ Store the option ID
      }
    }
    // --- LOGIC 3: productVariant ---
    else if (
      product.productVariant &&
      product.productVariant.length > 0 &&
      selectedOption?.type === "productVariant"
    ) {
      const opt = selectedOption.value;
      // ✅ opt.price = Retail Price, apply discount to get selling price later
      price = parseFloat(opt.price) || 0;
      discountMode = isB2C ? opt.b2cDiscountMode : opt.b2bDiscountMode;
      discountValue = isB2C
        ? parseFloat(opt.b2cDiscountValue) || 0
        : parseFloat(opt.b2bDiscountValue) || 0;
      deliveryCharge = parseFloat(opt.deliveryCharge) || deliveryCharge;
      variantName = opt.variantName || opt.name || opt.title || "";
      variantId = opt.id;
      qty = 1;
    }
    // --- LOGIC 1: Simple Product (No variants) ---
    else {
      // ✅ product.price = MRP, apply discount from product level
      price = parseFloat(product.price) || 0;
      discountMode = isB2C ? product.b2cDiscountMode : product.b2bDiscountMode;
      discountValue = isB2C
        ? parseFloat(product.b2cDiscountValue) || 0
        : parseFloat(product.b2bDiscountValue) || 0;
      qty = 1;
    }
    // ✅=== PRICING LOGIC END ===✅

    const newItem = {
      productId,
      variantId: variantId || null,
      variantName: variantName || null,
      qty: qty,
      price: price, // For Logic1&3: Retail Price; For Logic2: Selling Price
      discountMode: discountMode,
      discountValue: discountValue, // ✅ Store RAW value - cart logic applies it
      deliveryCharge: deliveryCharge,
      name: itemName,
      mainImage: product.mainImage,
      status: product.status,
      shopStatus: product.shopStatus,
    };

    // Cart calculation helper
    const calculateCartTotals = (items) => {
      const safeFloat = (val) => (isNaN(parseFloat(val)) ? 0 : parseFloat(val));
      const itemsPrice = items.reduce(
        (sum, item) => sum + safeFloat(item.price) * parseInt(item.qty || 1),
        0,
      );

      const totalDiscountValue = items.reduce((sum, item) => {
        let d = 0;
        const mode = (item.discountMode || "").toLowerCase();
        const dVal = parseFloat(item.discountValue) || 0;
        const pPrice = parseFloat(item.price) || 0;
        const pQty = parseInt(item.qty || 1);

        // ✅ Only apply discount if discountMode exists AND price is retail (Logic 1&3)
        // For Logic 2 (qtyAndPrice), discountMode is null, so no calculation
        if (mode === "percent" && item.discountMode) {
          d = (dVal / 100) * pPrice * pQty;
        } else if (mode === "amount" && item.discountMode) {
          d = dVal * pQty;
        }
        return sum + d;
      }, 0);

      const totalDeliveryCharge = items.reduce(
        (sum, item) =>
          sum + safeFloat(item.deliveryCharge) * parseInt(item.qty || 1),
        0,
      );

      return {
        itemsPrice,
        totalDiscountValue,
        totalDeliveryCharge,
        totalPrice: itemsPrice - totalDiscountValue + totalDeliveryCharge,
      };
    };

    let updatedCart;
    if (currentCart) {
      const updatedItems = [...currentCart.cartItems, newItem];
      const totals = calculateCartTotals(updatedItems);

      const data = {
        customerId,
        ...totals,
        discountMode: newItem.discountMode,
        cartItems: updatedItems,
      };
      const res = await api.put(
        `/cart/?id=${currentCart.id}`,
        data,
        customerReqConfig(),
      );
      if (res.status === 200) updatedCart = res.data.data;
    } else {
      const totals = calculateCartTotals([newItem]);
      const data = {
        customerId,
        ...totals,
        discountMode: discountMode,
        cartItems: [newItem],
      };
      const res = await api.post("/cart/", data, customerReqConfig());
      if (res.status === 200) updatedCart = res.data.data;
    }

    if (updatedCart) {
      if (optionalSetCartData) optionalSetCartData([updatedCart]);
      if (setAddedToCartMap)
        setAddedToCartMap((prev) => ({ ...prev, [productId]: true }));
      fetchCartCount?.();
      window.dispatchEvent(new Event("cartUpdated"));
      toast({ type: "success", message: "Item added to cart!" });
    }
  } catch (err) {
    console.error("Add to cart error:", err);
    toast({
      type: "error",
      message: err?.response?.data?.message || "Failed to add item to cart",
    });
  } finally {
    setLoading(null);
  }
};

//  Wishlist handler (No changes needed here based on request)
export const handleWishlist = async ({
  item,
  customer,
  wishlistProductIds,
  setWishlistProductIds,
  setTotalWishlistItems,
  toast,
}) => {
  const customerId = customer?.id;
  const productId = String(item?.productId || item?.id);
  if (!customerId || !productId) {
    toast({ type: "error", message: "Missing customer or product ID" });
    return;
  }
  try {
    let currentWishlist = null;
    try {
      const res = await api.get(
        `/wishlist/?customerId=${customerId}`,
        customerReqConfig(),
      );
      const data = res.data.data;
      const list = Array.isArray(data) ? data : [data];
      currentWishlist =
        list.find((w) => w.status === "active") || list[0] || null;
    } catch (err) {
      console.error("Failed to fetch wishlist:", err);
    }
    const isCurrentlyWishlisted = currentWishlist?.products?.some(
      (p) => String(p.id) === String(productId),
    );
    if (isCurrentlyWishlisted) {
      await api.delete(
        `/wishlist/?customerId=${customerId}&productId=${productId}`,
        customerReqConfig(),
      );
      setWishlistProductIds((prev) => prev.filter((id) => id !== productId));
      setTotalWishlistItems((prev) => Math.max(prev - 1, 0));
      toast({ type: "success", message: "Removed from wishlist" });
    } else {
      const data = { customerId, productId };
      const res = await api.post("/wishlist/", data, customerReqConfig());
      setWishlistProductIds((prev) => [...prev, productId]);
      setTotalWishlistItems((prev) => prev + 1);
      toast({ type: "success", message: res.data.message });
    }
    window.dispatchEvent(new Event("wishlistUpdated"));
  } catch (error) {
    const msg = error?.response?.data?.message || error.message;
    toast({ type: "error", message: msg });
  }
};
